import { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Eye,
  Users,
  Calendar,
  BarChart2,
  Activity,
  Award,
  Clock,
  Check,
  EyeOff
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { DailyTrafficPoint } from "../types";

interface TrafficOverviewCardProps {
  initialHistory?: DailyTrafficPoint[];
  todayViews?: number;
  yesterdayViews?: number;
  views7Days?: number;
  growth7d?: number;
  dailyAverage?: number;
  peakDay?: { date: string; label: string; views: number };
}

export function TrafficOverviewCard({
  initialHistory = [],
  todayViews = 0,
  yesterdayViews = 0,
  views7Days = 0,
  growth7d = 0,
  dailyAverage = 0,
  peakDay
}: TrafficOverviewCardProps) {
  const [timeRange, setTimeRange] = useState<7 | 14 | 30>(14);
  const [chartType, setChartType] = useState<"area" | "bar">("area");
  
  // Interactive Series Visibility state for clicking legend
  const [seriesVisibility, setSeriesVisibility] = useState<{
    views: boolean;
    visitors: boolean;
  }>({
    views: true,
    visitors: true
  });

  const toggleSeries = (key: "views" | "visitors") => {
    setSeriesVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      // If user tries to turn off both, switch the other one on so the chart is never totally empty
      if (!next.views && !next.visitors) {
        return {
          views: key !== "views",
          visitors: key !== "visitors"
        };
      }
      return next;
    });
  };

  // Filter history according to selected time range
  const filteredData = useMemo(() => {
    if (!initialHistory || initialHistory.length === 0) {
      const now = new Date();
      return Array.from({ length: timeRange }, (_, idx) => {
        const d = new Date(now.getTime() - (timeRange - 1 - idx) * 86400000);
        return {
          date: d.toISOString().substring(0, 10),
          label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          fullDate: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
          weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
          views: 0,
          visitors: 0,
          articles: 0
        };
      });
    }
    return initialHistory.slice(-timeRange);
  }, [initialHistory, timeRange]);

  // Computed summary metrics for the selected time range
  const metrics = useMemo(() => {
    const totalViews = filteredData.reduce((acc, curr) => acc + curr.views, 0);
    const totalVisitors = filteredData.reduce((acc, curr) => acc + curr.visitors, 0);
    const avg = filteredData.length > 0 ? Math.round(totalViews / filteredData.length) : 0;

    let peak = { label: "N/A", views: 0, fullDate: "" };
    for (const point of filteredData) {
      if (point.views >= peak.views) {
        peak = { label: point.label, views: point.views, fullDate: point.fullDate };
      }
    }

    return {
      totalViews,
      totalVisitors,
      avg,
      peak
    };
  }, [filteredData]);

  // Custom Interactive Legend Component
  const renderInteractiveLegend = () => {
    return (
      <div className="traffic-interactive-legend">
        <div className="legend-hint-label">
          <span>Click series to filter:</span>
        </div>

        <button
          type="button"
          className={`traffic-legend-chip chip-views ${seriesVisibility.views ? "active" : "muted"}`}
          onClick={() => toggleSeries("views")}
          aria-pressed={seriesVisibility.views}
          title="Click to show/hide Article Views"
        >
          <span className="chip-indicator bg-emerald">
            {seriesVisibility.views ? <Check size={10} strokeWidth={3} /> : <EyeOff size={10} />}
          </span>
          <span className="chip-label">Article Views</span>
          <span className="chip-count">{metrics.totalViews.toLocaleString()}</span>
        </button>

        <button
          type="button"
          className={`traffic-legend-chip chip-visitors ${seriesVisibility.visitors ? "active" : "muted"}`}
          onClick={() => toggleSeries("visitors")}
          aria-pressed={seriesVisibility.visitors}
          title="Click to show/hide Unique Readers"
        >
          <span className="chip-indicator bg-sky">
            {seriesVisibility.visitors ? <Check size={10} strokeWidth={3} /> : <EyeOff size={10} />}
          </span>
          <span className="chip-label">Unique Readers</span>
          <span className="chip-count">{metrics.totalVisitors.toLocaleString()}</span>
        </button>
      </div>
    );
  };

  return (
    <motion.div
      className="admin-traffic-card"
      id="admin-traffic-overview"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* Header with Title & Interactive Controls */}
      <div className="traffic-card-header">
        <div className="traffic-header-title-wrap">
          <div className="traffic-header-badge">
            <span className="traffic-live-indicator" />
            <Activity size={15} />
            <span>REAL-TIME TELEMETRY</span>
          </div>
          <h2 className="traffic-title">Traffic & Readership Overview</h2>
          <p className="traffic-subtitle">
            Daily verified article impressions, reader sessions, and audience engagement trajectory.
          </p>
        </div>

        <div className="traffic-controls">
          {/* Chart Type Toggle */}
          <div className="traffic-control-group chart-mode-toggle" role="group" aria-label="Chart mode">
            <button
              type="button"
              className={`traffic-btn-toggle ${chartType === "area" ? "active" : ""}`}
              onClick={() => setChartType("area")}
              title="Area Trend Curve"
            >
              <Activity size={14} />
              <span>Curve</span>
            </button>
            <button
              type="button"
              className={`traffic-btn-toggle ${chartType === "bar" ? "active" : ""}`}
              onClick={() => setChartType("bar")}
              title="Bar Column Chart"
            >
              <BarChart2 size={14} />
              <span>Bars</span>
            </button>
          </div>

          {/* Quick Filter Pill */}
          <button
            type="button"
            className={`traffic-pill-toggle ${seriesVisibility.visitors && seriesVisibility.views ? "active" : ""}`}
            onClick={() => {
              if (seriesVisibility.views && seriesVisibility.visitors) {
                setSeriesVisibility({ views: true, visitors: false });
              } else {
                setSeriesVisibility({ views: true, visitors: true });
              }
            }}
            title="Toggle All / Views Only"
          >
            <Users size={13} />
            <span>{seriesVisibility.visitors ? "Dual Series" : "Views Only"}</span>
          </button>

          {/* Timeframe Switcher */}
          <div className="traffic-timeframe-selector" role="group" aria-label="Timeframe">
            <button
              type="button"
              className={`time-btn ${timeRange === 7 ? "active" : ""}`}
              onClick={() => setTimeRange(7)}
            >
              7D
            </button>
            <button
              type="button"
              className={`time-btn ${timeRange === 14 ? "active" : ""}`}
              onClick={() => setTimeRange(14)}
            >
              14D
            </button>
            <button
              type="button"
              className={`time-btn ${timeRange === 30 ? "active" : ""}`}
              onClick={() => setTimeRange(30)}
            >
              30D
            </button>
          </div>
        </div>
      </div>

      {/* Key Metric Highlights Row with Entrance Stagger */}
      <div className="traffic-kpi-grid">
        <motion.div
          className="traffic-kpi-item primary"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
        >
          <div className="kpi-icon-wrap bg-emerald">
            <Eye size={18} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">{timeRange}-Day Total Views</span>
            <div className="kpi-value-row">
              <strong className="kpi-value">{metrics.totalViews.toLocaleString()}</strong>
              {growth7d !== 0 && timeRange <= 14 && (
                <span className={`kpi-trend-pill ${growth7d >= 0 ? "positive" : "negative"}`}>
                  {growth7d >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  <span>{growth7d > 0 ? `+${growth7d}%` : `${growth7d}%`}</span>
                </span>
              )}
            </div>
            <span className="kpi-subtext">Verified editorial reads</span>
          </div>
        </motion.div>

        <motion.div
          className="traffic-kpi-item"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          <div className="kpi-icon-wrap bg-sky">
            <Users size={18} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Unique Readers</span>
            <strong className="kpi-value">{metrics.totalVisitors.toLocaleString()}</strong>
            <span className="kpi-subtext">Estimated unique devices</span>
          </div>
        </motion.div>

        <motion.div
          className="traffic-kpi-item"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
        >
          <div className="kpi-icon-wrap bg-amber">
            <Clock size={18} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Daily Average</span>
            <strong className="kpi-value">{metrics.avg.toLocaleString()}</strong>
            <span className="kpi-subtext">Views per 24 hours</span>
          </div>
        </motion.div>

        <motion.div
          className="traffic-kpi-item"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.2 }}
        >
          <div className="kpi-icon-wrap bg-purple">
            <Award size={18} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Peak Traffic Day</span>
            <strong className="kpi-value">{metrics.peak.views.toLocaleString()}</strong>
            <span className="kpi-subtext">{metrics.peak.fullDate || metrics.peak.label}</span>
          </div>
        </motion.div>
      </div>

      {/* Visual Chart Canvas with Entrance & Mode Transition */}
      <div className="traffic-chart-container">
        {/* Interactive Legend Header */}
        {renderInteractiveLegend()}

        <AnimatePresence mode="wait">
          <motion.div
            key={`${chartType}-${timeRange}`}
            initial={{ opacity: 0, scale: 0.99 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.99 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ width: "100%", height: 300 }}
          >
            <ResponsiveContainer width="100%" height={300}>
              {chartType === "area" ? (
                <AreaChart data={filteredData} margin={{ top: 10, right: 15, left: -10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#059669" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="visitorsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0284c7" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#0284c7" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148, 163, 184, 0.2)" />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={{ stroke: "rgba(148, 163, 184, 0.3)" }}
                    tick={{ fill: "#64748b", fontSize: 11, fontWeight: 500 }}
                    dy={6}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickFormatter={(val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val)}
                  />
                  <Tooltip content={<CustomChartTooltip />} />

                  {seriesVisibility.views && (
                    <Area
                      name="Article Views"
                      type="monotone"
                      dataKey="views"
                      stroke="#059669"
                      strokeWidth={2.5}
                      fillOpacity={1}
                      fill="url(#viewsGradient)"
                      activeDot={{ r: 6, fill: "#059669", stroke: "#ffffff", strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={850}
                      animationEasing="ease-out"
                    />
                  )}
                  {seriesVisibility.visitors && (
                    <Area
                      name="Unique Readers"
                      type="monotone"
                      dataKey="visitors"
                      stroke="#0284c7"
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      fillOpacity={1}
                      fill="url(#visitorsGradient)"
                      activeDot={{ r: 5, fill: "#0284c7", stroke: "#ffffff", strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={850}
                      animationEasing="ease-out"
                    />
                  )}
                </AreaChart>
              ) : (
                <BarChart data={filteredData} margin={{ top: 10, right: 15, left: -10, bottom: 5 }} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148, 163, 184, 0.2)" />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={{ stroke: "rgba(148, 163, 184, 0.3)" }}
                    tick={{ fill: "#64748b", fontSize: 11, fontWeight: 500 }}
                    dy={6}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickFormatter={(val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val)}
                  />
                  <Tooltip content={<CustomChartTooltip />} />

                  {seriesVisibility.views && (
                    <Bar
                      name="Article Views"
                      dataKey="views"
                      fill="#059669"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={32}
                      isAnimationActive={true}
                      animationDuration={800}
                      animationEasing="ease-out"
                    />
                  )}
                  {seriesVisibility.visitors && (
                    <Bar
                      name="Unique Readers"
                      dataKey="visitors"
                      fill="#0284c7"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={32}
                      isAnimationActive={true}
                      animationDuration={800}
                      animationEasing="ease-out"
                    />
                  )}
                </BarChart>
              )}
            </ResponsiveContainer>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer Meta Details */}
      <div className="traffic-card-footer">
        <div className="traffic-footer-left">
          <span className="footer-status-pill">
            <span className="pulse-dot-emerald" />
            Active Session Stream
          </span>
          <span className="footer-stat-meta">
            Today: <strong>{todayViews.toLocaleString()} views</strong> (Yesterday: {yesterdayViews.toLocaleString()})
          </span>
        </div>
        <div className="traffic-footer-right">
          <span>Updates automatically with real reader impressions</span>
        </div>
      </div>
    </motion.div>
  );
}

function CustomChartTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload as DailyTrafficPoint;
    return (
      <div className="traffic-custom-tooltip">
        <div className="tooltip-header">
          <Calendar size={13} />
          <span>{data.fullDate || `${data.weekday}, ${data.label}`}</span>
        </div>
        <div className="tooltip-body">
          {payload.map((item: any) => {
            const isViews = item.dataKey === "views";
            return (
              <div key={item.dataKey} className={`tooltip-item ${isViews ? "views" : "visitors"}`}>
                <span className={`tooltip-dot ${isViews ? "bg-emerald" : "bg-sky"}`} />
                <span className="tooltip-item-label">{isViews ? "Article Views:" : "Unique Readers:"}</span>
                <strong className="tooltip-item-val">{Number(item.value).toLocaleString()}</strong>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
}
